import React from "react"
import * as DOMPurify from "dompurify"
import styles from "./HTMLPreviewEditor.module.css"

const HTML_NODE_TYPE = {
    ELEMENT_NODE: 1
}

const isHtmlValid = (html) => {
    const doc = new DOMParser().parseFromString(html, "text/html")
    return Array.from(doc.body.childNodes).some((node) => node.nodeType === HTML_NODE_TYPE.ELEMENT_NODE)
}

const decodeHtml = (html: string) => {
    const txt = document.createElement("textarea")
    txt.innerHTML = html
    return txt.value
}

const renderHTML = (htmlContent: string) => {
    let decodedHtml = decodeHtml(htmlContent.replace(/\\n/g, "\n"))

    if (isHtmlValid(decodedHtml)) {
        return decodedHtml
    }
    if (isHtmlValid(htmlContent)) {
        return htmlContent
    }

    return decodedHtml
}

/** Inline preview: body markup + head styles so emails keep typography (no iframe document shell). */
const toInlineDocumentHtml = (html: string) => {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const headStyles = Array.from(doc.head?.querySelectorAll("style") ?? [])
        .map((el) => el.outerHTML)
        .join("")
    const bodyHtml = doc.body?.innerHTML ?? html
    return headStyles + bodyHtml
}

const purifyInlineHtml = (html: string) =>
    DOMPurify.sanitize(toInlineDocumentHtml(html), {
        ADD_TAGS: ["iframe", "style"],
        ADD_ATTR: ["allow", "allowfullscreen", "frameborder", "scrolling", "type", "media"]
    })

const PreviewPane: React.FC<{
    markdown: string
    isFullScreen?: boolean
    /** `inline` renders in the document (full natural height, no iframe chrome). */
    variant?: "iframe" | "inline"
}> = ({markdown, isFullScreen = false, variant = "iframe"}) => {
    const processedHtml = renderHTML(markdown)

    if (variant === "inline") {
        return (
            <div className={styles.inlinePreview} dangerouslySetInnerHTML={{__html: purifyInlineHtml(processedHtml)}} />
        )
    }

    return (
        <iframe
            title="Preview"
            srcDoc={processedHtml}
            width="100%"
            height="100%"
            className={isFullScreen ? styles.IFrameFullscreenStyles : styles.IFrameDefaultStyles}
        />
    )
}

export default PreviewPane
